/** @module @liquidefy/code-editor/layout/layout-decorations @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  DecorationId,
  DecorationData,
  DecorationOptions,
  MarkerStickiness,
  Range,
  Position,
  EditorResult,
  MinimapDecorationOptions,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asDecorationId,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — decoration kinds, state, overview ruler
// ═══════════════════════════════════════════════════════════════════════════════

/** Classifies where and how a decoration is rendered. */
export type DecorationKind =
  | 'inline'         // CSS class applied to inline text spans
  | 'line'           // CSS class applied to the entire line element
  | 'gutter'         // Icon/class rendered in the line-number gutter margin
  | 'overview-ruler' // Colored tick in the scrollbar overview ruler
  | 'minimap';       // Colored block in the minimap

/** A decoration specific to the overview ruler (scrollbar track). */
export interface OverviewRulerDecoration {
  readonly id: DecorationId;
  readonly color: string;
  readonly range: Range;
  readonly position: 'left' | 'center' | 'right' | 'full';
  readonly lane: 'left' | 'right' | 'center' | 'full';
  readonly ownerId: string;
}

/** A decoration rendered inline within text content. */
export interface InlineDecoration {
  readonly id: DecorationId;
  readonly range: Range;
  readonly className: string;
  readonly beforeContentText?: string;
  readonly afterContentText?: string;
  readonly hoverMessage?: string;
  readonly priority: number;
  readonly ownerId: string;
}

/** A decoration rendered in the gutter (left of line numbers). */
export interface GutterDecoration {
  readonly id: DecorationId;
  readonly line: number;
  readonly glyphMarginClassName: string;
  readonly tooltip?: string;
  readonly priority: number;
  readonly ownerId: string;
}

/** The full decoration container for an editor view. */
export interface DecorationState {
  readonly decorations: DecorationData[];
  readonly overviewRuler: OverviewRulerDecoration[];
  readonly inline: InlineDecoration[];
  readonly gutter: GutterDecoration[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create an empty DecorationState with no decorations of any kind.
 */
export function createDecorationState(): DecorationState {
  return {
    decorations: [],
    overviewRuler: [],
    inline: [],
    gutter: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 CORE DECORATION CRUD — add, remove, update
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a decoration to the state.
 * The decoration's ID is generated automatically — the caller provides range and options.
 *
 * @param state      Current decoration state.
 * @param decoration Partial decoration data (id will be generated).
 */
export function addDecoration(
  state: DecorationState,
  decoration: Omit<DecorationData, 'id'> & { readonly ownerId?: string },
): EditorResult<DecorationState> {
  const id = asDecorationId(generateId('dec'));
  const newDecoration: DecorationData = { id, ...decoration };
  return ok(
    { ...state, decorations: [...state.decorations, newDecoration] },
    'DECORATION_ADDED',
    `Decoration added`,
    [],
  );
}

/**
 * Remove a decoration by its ID.
 * Also removes any overview ruler, inline, or gutter entries for the same ID.
 *
 * @param state        Current decoration state.
 * @param decorationId The ID of the decoration to remove.
 */
export function removeDecoration(
  state: DecorationState,
  decorationId: DecorationId,
): EditorResult<DecorationState> {
  const idx = state.decorations.findIndex(d => d.id === decorationId);
  if (idx === -1) {
    return err('DECORATION_NOT_FOUND', `No decoration with id: ${decorationId}`);
  }
  return ok(
    {
      decorations: state.decorations.filter(d => d.id !== decorationId),
      overviewRuler: state.overviewRuler.filter(d => d.id !== decorationId),
      inline: state.inline.filter(d => d.id !== decorationId),
      gutter: state.gutter.filter(d => d.id !== decorationId),
    },
    'DECORATION_REMOVED',
    'Decoration removed',
    [],
  );
}

/**
 * Update the options of an existing decoration in-place.
 * Returns an error if the decoration does not exist.
 *
 * @param state        Current decoration state.
 * @param decorationId The ID of the decoration to update.
 * @param changes      Partial options to merge into the decoration.
 */
export function updateDecoration(
  state: DecorationState,
  decorationId: DecorationId,
  changes: Partial<DecorationOptions>,
): EditorResult<DecorationState> {
  const idx = state.decorations.findIndex(d => d.id === decorationId);
  if (idx === -1) {
    return err('DECORATION_NOT_FOUND', `No decoration with id: ${decorationId}`);
  }
  const existing = state.decorations[idx]!;
  const updated: DecorationData = {
    id: existing.id,
    range: existing.range,
    options: { ...existing.options, ...changes },
  };
  const decorations = [
    ...state.decorations.slice(0, idx),
    updated,
    ...state.decorations.slice(idx + 1),
  ];
  return ok({ ...state, decorations }, 'DECORATION_UPDATED', 'Decoration updated', []);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 RANGE QUERIES — by line, by range, by owner
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get all decorations that cover (even partially) the given line number.
 * Line numbers are zero-based.
 *
 * @param state      Current decoration state.
 * @param lineNumber The zero-based line to query.
 */
export function getDecorationsForLine(
  state: DecorationState,
  lineNumber: number,
): DecorationData[] {
  return state.decorations.filter(d =>
    d.range.start.line <= lineNumber && d.range.end.line >= lineNumber,
  );
}

/**
 * Get all decorations whose ranges overlap with the given range.
 * Overlap is detected when: decStart <= queryEnd && decEnd >= queryStart.
 *
 * @param state Current decoration state.
 * @param range The range to query against.
 */
export function getDecorationsForRange(
  state: DecorationState,
  range: Range,
): DecorationData[] {
  return state.decorations.filter(d =>
    rangesOverlap(d.range, range),
  );
}

/**
 * Remove all decorations owned by a specific owner ID in bulk.
 * Useful for clearing all decorations from a single extension or tool.
 *
 * @param state   Current decoration state.
 * @param ownerId The owner identifier to clear.
 */
export function clearDecorationsByOwner(
  state: DecorationState,
  ownerId: string,
): DecorationState {
  // For the main decorations list, filter by matching hoverMessage as a proxy
  // for owner (ownerId is not a field on DecorationData, so we track via inline/ruler/gutter).
  const overviewRuler = state.overviewRuler.filter(d => d.ownerId !== ownerId);
  const inline = state.inline.filter(d => d.ownerId !== ownerId);
  const gutter = state.gutter.filter(d => d.ownerId !== ownerId);

  // Build set of IDs that remain
  const remainingIds = new Set([
    ...overviewRuler.map(d => d.id),
    ...inline.map(d => d.id),
    ...gutter.map(d => d.id),
    // Keep decorations that aren't in any of the typed lists (plain CSS decorations)
  ]);

  // Decorations without explicit owner tracking are kept as-is
  const decorations = state.decorations;

  return { decorations, overviewRuler, inline, gutter };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 OVERVIEW RULER — add, query
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add an overview ruler decoration (colored tick in the scrollbar track).
 * The color should be a CSS color string (hex, rgba, etc.).
 *
 * @param state      Current decoration state.
 * @param decoration The ruler decoration to add.
 */
export function addOverviewRulerDecoration(
  state: DecorationState,
  decoration: Omit<OverviewRulerDecoration, 'id'>,
): EditorResult<DecorationState> {
  const id = asDecorationId(generateId('ruler'));
  const ruler: OverviewRulerDecoration = { id, ...decoration };
  return ok(
    { ...state, overviewRuler: [...state.overviewRuler, ruler] },
    'RULER_DECORATION_ADDED',
    'Overview ruler decoration added',
    [],
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 MINIMAP — decorations visible in the minimap
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Return all decorations that are configured to appear in the minimap.
 * A decoration appears in the minimap when its options include a `minimap` block.
 *
 * @param state Current decoration state.
 */
export function getMinimapDecorations(
  state: DecorationState,
): Array<DecorationData & { readonly minimap: MinimapDecorationOptions }> {
  return state.decorations.filter(
    (d): d is DecorationData & { readonly minimap: MinimapDecorationOptions } =>
      d.options.minimap !== undefined,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 PRIORITY & MERGING — sort by z-index, combine overlapping inline decs
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Compute the display priority order for a list of inline decorations.
 * Higher `priority` values are rendered on top (later in the sorted array).
 * Decorations with equal priority are sorted by range start line then column.
 *
 * @param decorations The inline decorations to sort.
 */
export function computeDecorationPriority(decorations: InlineDecoration[]): InlineDecoration[] {
  return [...decorations].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    if (a.range.start.line !== b.range.start.line) {
      return a.range.start.line - b.range.start.line;
    }
    return a.range.start.column - b.range.start.column;
  });
}

/**
 * Merge overlapping inline decorations that share a class name.
 * When two decorations with the same class cover overlapping ranges, the union
 * of their ranges is used and a single merged decoration is emitted.
 * Non-overlapping decorations are returned unchanged.
 *
 * @param decorations The inline decorations to merge.
 */
export function mergeInlineDecorations(decorations: InlineDecoration[]): InlineDecoration[] {
  if (decorations.length === 0) return [];

  const grouped = new Map<string, InlineDecoration[]>();
  for (const dec of decorations) {
    const key = `${dec.ownerId}::${dec.className}`;
    const bucket = grouped.get(key) ?? [];
    bucket.push(dec);
    grouped.set(key, bucket);
  }

  const result: InlineDecoration[] = [];
  for (const [, bucket] of grouped) {
    const sorted = [...bucket].sort((a, b) =>
      a.range.start.line !== b.range.start.line
        ? a.range.start.line - b.range.start.line
        : a.range.start.column - b.range.start.column,
    );

    let current = sorted[0]!;
    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i]!;
      if (rangesOverlap(current.range, next.range)) {
        // Merge: take the union of the two ranges
        current = {
          ...current,
          range: {
            start: positionMin(current.range.start, next.range.start),
            end: positionMax(current.range.end, next.range.end),
          },
        } as InlineDecoration;
      } else {
        result.push(current);
        current = next;
      }
    }
    result.push(current);
  }

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/** Return true when two ranges have any overlap (inclusive of touching endpoints). */
function rangesOverlap(a: Range, b: Range): boolean {
  return !(
    a.end.line < b.start.line ||
    (a.end.line === b.start.line && a.end.column < b.start.column) ||
    b.end.line < a.start.line ||
    (b.end.line === a.start.line && b.end.column < a.start.column)
  );
}

/** Return the position that comes first in document order. */
function positionMin(a: Position, b: Position): Position {
  if (a.line < b.line) return a;
  if (b.line < a.line) return b;
  return a.column <= b.column ? a : b;
}

/** Return the position that comes last in document order. */
function positionMax(a: Position, b: Position): Position {
  if (a.line > b.line) return a;
  if (b.line > a.line) return b;
  return a.column >= b.column ? a : b;
}
